import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MessageCircle, Send } from 'lucide-react';
import SelectMenu from '../components/SelectMenu';

function Support() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real app, this would send to your backend
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        <div className="card-glass mb-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl glass-strong flex items-center justify-center glow-red">
              <MessageCircle className="w-8 h-8 text-rebel-red" />
            </div>
            <div>
              <h1 className="text-4xl font-black text-gradient">Support Center</h1>
              <p className="text-gray-400 mt-1">We're here to help you succeed</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Contact Info */}
          <div className="card-glass">
            <h2 className="text-2xl font-bold mb-6">Get in Touch</h2>
            
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Mail className="w-5 h-5 text-rebel-blue" />
                  <h3 className="font-semibold text-white">Email Support</h3>
                </div>
                <a
                  href="mailto:Support@RebelPrompts.com"
                  className="text-rebel-blue text-lg hover:underline block"
                >
                  Support@RebelPrompts.com
                </a>
                <p className="text-sm text-gray-400 mt-1">
                  We typically respond within 24 hours
                </p>
              </div>

              <div className="pt-6 border-t border-white/10">
                <h3 className="font-semibold text-white mb-3">Support Hours</h3>
                <p className="text-gray-300">Monday - Friday: 9AM - 6PM EST</p>
                <p className="text-gray-300">Saturday - Sunday: Limited support</p>
              </div>

              <div className="pt-6 border-t border-white/10">
                <h3 className="font-semibold text-white mb-3">Quick Links</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => navigate('/docs')}
                    className="block text-rebel-blue hover:underline"
                  >
                    → Documentation
                  </button>
                  <button
                    onClick={() => navigate('/terms')}
                    className="block text-rebel-blue hover:underline"
                  >
                    → Terms of Service
                  </button>
                  <button
                    onClick={() => navigate('/privacy')}
                    className="block text-rebel-blue hover:underline"
                  >
                    → Privacy Policy
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="card-glass">
            <h2 className="text-2xl font-bold mb-6">Send us a Message</h2>
            
            {submitted ? (
              <div className="p-6 rounded-xl bg-green-500/10 border border-green-500/30 text-center">
                <div className="text-4xl mb-3">✅</div>
                <h3 className="text-xl font-bold text-green-400 mb-2">Message Sent!</h3>
                <p className="text-gray-300">
                  We've received your message and will respond to{' '}
                  <span className="text-white font-semibold">{formData.email}</span> soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">
                    Your Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="input-glass"
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-glass"
                    placeholder="you@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">
                    Subject
                  </label>
                  <SelectMenu
                    value={formData.subject}
                    options={[
                      { value: 'account', label: 'Account & Billing' },
                      { value: 'technical', label: 'Technical Issue' },
                      { value: 'feature', label: 'Feature Request' },
                      { value: 'credits', label: 'Credits & Pricing' },
                      { value: 'other', label: 'Other' }
                    ]}
                    placeholder="Select a topic"
                    onChange={(value) =>
                      setFormData((prev) => ({ ...prev, subject: value }))
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="input-glass"
                    rows="6"
                    placeholder="How can we help you?"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  <Send className="w-5 h-5" />
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Common Issues */}
        <div className="card-glass">
          <h2 className="text-2xl font-bold mb-6">Common Issues & Solutions</h2>
          
          <div className="space-y-6">
            <div className="pb-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white mb-2">Can't login to my account</h3>
              <p className="text-gray-300">
                Try resetting your password and signing in again with the email address you registered with.
                Email <a href="mailto:Support@RebelPrompts.com" className="text-rebel-blue hover:underline">Support@RebelPrompts.com</a> if issues persist.
              </p>
            </div>

            <div className="pb-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white mb-2">Credits not working</h3>
              <p className="text-gray-300">
                Check your credit balance in your profile. Monthly subscription credits renew at the start of each billing cycle.
                Additional purchased credits don't expire. Contact support if you believe there's an error.
              </p>
            </div>

            <div className="pb-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white mb-2">AI generation failed</h3>
              <p className="text-gray-300">
                Ensure you have sufficient credits and try again. Some prompts may be rejected by AI providers due to content policies.
                If the issue persists, contact support with details.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">How to delete my account</h3>
              <p className="text-gray-300">
                Go to Settings → Account → Delete Account. This action is permanent and cannot be undone. All your prompts 
                and data will be permanently deleted. Contact{' '}
                <a href="mailto:Support@RebelPrompts.com" className="text-rebel-blue hover:underline">
                  Support@RebelPrompts.com
                </a>{' '}
                if you need assistance.
              </p>
            </div>
          </div>
        </div>

        {/* Emergency Support */}
        <div className="mt-12 text-center card-glass border-rebel-red/30">
          <h2 className="text-2xl font-bold mb-3">Need Immediate Help?</h2>
          <p className="text-gray-400 mb-6">
            For urgent issues affecting your account or service availability
          </p>
          <a
            href="mailto:Support@RebelPrompts.com?subject=Urgent%3A%20"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Mail className="w-5 h-5" />
            Email Support Now
          </a>
        </div>
      </div>
    </div>
  );
}

export default Support;
